/**
 * Registration Component - Registry API integration
 * Handles cat registration with SACC/CASA/CFSA
 */

import { useState, useEffect } from 'react';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Send,
  Download,
  Plus,
  ArrowRight
} from 'lucide-react';
import { Card, Button, Badge, Select, Alert, Modal, StatCard, Tabs } from '../shared/UIComponents';
import { formatCurrency, formatDate, cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { 
  getRegistrationsByCattery, 
  getCatsByCattery,
  createRegistration,
  submitRegistration,
  SA_ASSOCIATIONS 
} from '../../firebase/firestore';

// Service fee rate (10%)
const SERVICE_FEE_RATE = 0.10;

export const RegistrationManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [cats, setCats] = useState([]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    if (user?.catteryId) {
      loadData();
    }
  }, [user?.catteryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [regsData, catsData] = await Promise.all([
        getRegistrationsByCattery(user.catteryId),
        getCatsByCattery(user.catteryId)
      ]);
      setRegistrations(regsData);
      setCats(catsData);
    } catch (error) {
      console.error('Error loading registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter registrations by status
  const pendingRegs = registrations.filter(r => r.status === 'pending' || r.status === 'submitted');
  const approvedRegs = registrations.filter(r => r.status === 'approved');
  const rejectedRegs = registrations.filter(r => r.status === 'rejected');

  // Calculate stats
  const stats = {
    total: registrations.length,
    pending: pendingRegs.length,
    approved: approvedRegs.length,
    totalFees: registrations.reduce((sum, r) => sum + (r.totalFee || 0), 0)
  };

  const tabs = [
    { id: 'pending', label: 'Pending', count: pendingRegs.length },
    { id: 'approved', label: 'Approved', count: approvedRegs.length },
    { id: 'all', label: 'All Registrations', count: registrations.length }
  ];

  const getFilteredRegistrations = () => {
    switch (activeTab) {
      case 'pending': return pendingRegs;
      case 'approved': return approvedRegs;
      default: return registrations;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">Registrations</h1>
          <p className="text-gray-500">Register cats with SACC, CASA, or CFSA</p>
        </div>
        <Button onClick={() => setShowNewModal(true)}>
          <Plus className="w-4 h-4" />
          New Registration
        </Button>
      </div>

      {/* Info Alert */}
      <Alert type="info" title="One-Click Registration">
        <p className="text-sm">
          Register your cats instantly with South African cat associations. 
          A <strong>10% service fee</strong> is added to cover automated PDF filing and processing.
          Pay the total fee (Registry Fee + Service Fee) at checkout.
        </p>
      </Alert>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Registrations"
          value={stats.total}
          icon={FileText}
        />
        <StatCard
          label="Pending"
          value={stats.pending}
          icon={Clock}
        />
        <StatCard
          label="Approved"
          value={stats.approved}
          icon={CheckCircle}
        />
        <StatCard
          label="Total Fees Paid"
          value={formatCurrency(stats.totalFees)}
          icon={FileText}
        />
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Registrations List */}
      <RegistrationsList 
        registrations={getFilteredRegistrations()} 
        cats={cats}
        loading={loading}
      />

      {/* New Registration Modal */}
      <NewRegistrationModal 
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        cats={cats.filter(c => !registrations.some(r => r.catId === c.id))}
        catteryId={user?.catteryId}
        onSuccess={() => {
          setShowNewModal(false);
          loadData();
        }}
      />
    </div>
  );
};

// Registrations List
const RegistrationsList = ({ registrations, cats, loading }) => {
  if (loading) {
    return (
      <Card>
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 bg-gray-200 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (registrations.length === 0) {
    return (
      <Card className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Registrations</h3>
        <p className="text-gray-500">Start by registering your first cat with an association.</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="divide-y divide-gray-200">
        {registrations.map((reg) => {
          const cat = cats.find(c => c.id === reg.catId);
          const association = SA_ASSOCIATIONS.find(a => a.code === reg.association);
          
          return (
            <div key={reg.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{cat?.name || 'Unknown Cat'}</h3>
                    <p className="text-sm text-gray-500">
                      {association?.name || reg.association}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Created {formatDate(reg.createdAt)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total Fee</p>
                    <p className="font-bold text-feline-600">{formatCurrency(reg.totalFee)}</p>
                  </div>
                  <RegistrationStatusBadge status={reg.status} />
                </div>
              </div>

              {/* Fee Breakdown */}
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-6 text-sm text-gray-500">
                  <span>Registry Fee: {formatCurrency(reg.fee)}</span>
                  <span>Service Fee (10%): {formatCurrency(reg.serviceFee)}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  {reg.status === 'pending' && (
                    <Button size="sm">
                      <Send className="w-3 h-3" />
                      Submit
                    </Button>
                  )}
                  {reg.pdfUrl && (
                    <Button variant="ghost" size="sm">
                      <Download className="w-3 h-3" />
                      PDF
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// Status Badge
const RegistrationStatusBadge = ({ status }) => {
  const configs = {
    pending: { label: 'Pending', color: 'warning' },
    submitted: { label: 'Submitted', color: 'primary' },
    approved: { label: 'Approved', color: 'success' },
    rejected: { label: 'Rejected', color: 'danger' }
  };
  
  const config = configs[status] || configs.pending;
  
  return <Badge variant={config.color}>{config.label}</Badge>;
};

// New Registration Modal
const NewRegistrationModal = ({ isOpen, onClose, cats, catteryId, onSuccess }) => {
  const [selectedCat, setSelectedCat] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Get selected association details
  const association = SA_ASSOCIATIONS.find(a => a.code === selectedAssociation);
  const registrationFee = association?.registrationFee || 0;
  const serviceFee = Math.round(registrationFee * SERVICE_FEE_RATE);
  const totalFee = registrationFee + serviceFee;

  const handleSubmit = async () => {
    if (!selectedCat || !selectedAssociation) {
      setError('Please select a cat and association');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      await createRegistration({
        catId: selectedCat,
        catteryId,
        association: selectedAssociation,
        fee: registrationFee
      });

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const catOptions = cats.map(c => ({
    value: c.id,
    label: `${c.name} (${c.breed})`
  }));

  const associationOptions = SA_ASSOCIATIONS.map(a => ({
    value: a.code,
    label: `${a.name} - ${formatCurrency(a.registrationFee)}`
  }));

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="New Registration"
      size="md"
    >
      <div className="space-y-4">
        {error && (
          <Alert type="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Select
          label="Select Cat"
          value={selectedCat}
          onChange={(e) => setSelectedCat(e.target.value)}
          options={catOptions}
          placeholder="Choose a cat to register"
        />

        <Select
          label="Select Association"
          value={selectedAssociation}
          onChange={(e) => setSelectedAssociation(e.target.value)}
          options={associationOptions}
          placeholder="Choose registry"
        />

        {selectedAssociation && (
          <Card className="bg-gray-50">
            <h4 className="font-semibold text-gray-900 mb-3">Fee Breakdown</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Registry Fee ({association?.name})</span>
                <span className="font-medium">{formatCurrency(registrationFee)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Service Fee (10%)</span>
                <span className="font-medium">{formatCurrency(serviceFee)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-bold text-feline-600">{formatCurrency(totalFee)}</span>
              </div>
            </div>
          </Card>
        )}

        <Alert type="info">
          <p className="text-sm">
            After payment, we'll automatically generate and submit the registration 
            form to {association?.name || 'the registry'}. You'll receive a confirmation 
            once approved.
          </p>
        </Alert>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            loading={submitting}
            disabled={!selectedCat || !selectedAssociation}
          >
            Pay {formatCurrency(totalFee)} & Submit
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RegistrationManager;
